import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { createCampaignType } from "../Services/APIService";
import { CampaignTypeFormData } from "../types/campaignType.types";

interface ValidationErrors {
  name?: string;
}

interface UseCreateCampaignTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CampaignTypeFormData) => void;
}

export const useCreateCampaignTypeModal = ({
  isOpen,
  onClose,
  onSubmit,
}: UseCreateCampaignTypeModalProps) => {
  const [formData, setFormData] = useState<CampaignTypeFormData>({
    name: "",
    isactive: true,
    createD_BY: Number(localStorage.getItem("userId")) || 1000,
    createD_ON: new Date().toISOString(),
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("authToken") || "";

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        isactive: true,
        createD_BY: Number(localStorage.getItem("userId")) || 1000,
        createD_ON: new Date().toISOString(),
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Campaign type name is required";
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Campaign type name must be at least 2 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleClearAll = () => {
    setFormData({
      name: "",
      isactive: true,
      createD_BY: Number(localStorage.getItem("userId")) || 1000,
      createD_ON: new Date().toISOString(),
    });
    setErrors({});
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const processedValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    setIsLoading(true);
    try {
      const trimmedData = {
        ...formData,
        name: formData.name.trim().replace(/\s+/g, " "),
        createD_ON: new Date().toISOString(),
      };

      const result = await createCampaignType(trimmedData);

      if (result && result.isCreated === false) {
        if (result.message?.includes("already exists")) {
          setErrors({ name: "Campaign type name already exists" });
          toast.error("Campaign type with this name already exists.");
        } else {
          toast.error(result.message || "Campaign type creation failed");
        }
        return;
      }

      if (result && result.isCreated === true) {
        onSubmit(trimmedData);
        //toast.success("Campaign type created successfully!");
        onClose();
      } else {
        toast.error("Campaign type creation failed");
      }
    } catch (error: any) {
      if (error.message.includes("already exists")) {
        setErrors({ name: "Campaign type name already exists" });
        toast.error("Campaign type with this name already exists.");
      } else {
        toast.error(error.message || "Failed to create campaign type");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    errors,
    isLoading,
    handleChange,
    handleClearAll,
    handleSubmit,
  };
};